import { supabase } from '../lib/supabase';
import type { SportStat } from '../types';

export type { SportStat };

/**
 * Service for managing sport-specific statistics
 */
class SportStatsService {
  /**
   * Get all stats for a player card
   */
  async getStats(playerCardId: string, teamId: string): Promise<SportStat[]> {
    const { data, error } = await supabase
      .from('sport_stats')
      .select('*')
      .eq('player_card_id', playerCardId)
      .eq('team_id', teamId)
      .order('date', { ascending: false });

    if (error) {
      console.error('[SportStatsService] Error fetching stats:', error);
      throw error;
    }

    return data || [];
  }

  /**
   * Add a new stat
   */
  async addStat(
    playerCardId: string,
    teamId: string,
    statKey: string,
    statValue: number,
    date: string,
    gameId?: string,
    opponent?: string,
    season?: string
  ): Promise<SportStat> {
    // If gameId is provided, use it; otherwise use opponent for grouping
    const insertData: any = {
      player_card_id: playerCardId,
      team_id: teamId,
      stat_key: statKey,
      stat_value: statValue,
      date: date,
      game_id: gameId || null,
      opponent: opponent || null,
      season: season || null,
    };

    const { data, error } = await supabase
      .from('sport_stats')
      .insert(insertData)
      .select()
      .single();

    if (error) {
      console.error('[SportStatsService] Error adding stat:', error);
      throw error;
    }

    return data;
  }

  /**
   * Replace a player's full stat line for one game in a single call (e.g. the
   * Tennis point-log projection, re-run on every set completion and again on
   * match completion). The point log is the source of truth, so this is
   * always a full replace of its current projection, never an increment.
   *
   * This is delete-then-insert, not a native upsert: the table's unique
   * indexes (idx_sport_stats_unique_player_stat_date[_no_game]) are PARTIAL
   * (split on game_id IS NULL), and Postgres only uses a partial index as an
   * ON CONFLICT arbiter when the conflict clause's own predicate matches —
   * something supabase-js's `.upsert()` has no way to express. A plain
   * `onConflict: 'player_card_id,stat_key,date,game_id'` reliably fails with
   * "there is no unique or exclusion constraint matching the ON CONFLICT
   * specification" for every single call, silently (caught upstream), which
   * is exactly what happened before this was caught.
   */
  async upsertGameStats(
    playerCardId: string,
    teamId: string,
    gameId: string,
    date: string,
    statValues: Record<string, number>,
    season?: string
  ): Promise<void> {
    const { error: deleteError } = await supabase
      .from('sport_stats')
      .delete()
      .eq('player_card_id', playerCardId)
      .eq('game_id', gameId);

    if (deleteError) {
      console.error('[SportStatsService] Error clearing previous game stats:', deleteError);
      throw deleteError;
    }

    const rows = Object.entries(statValues).map(([stat_key, stat_value]) => ({
      player_card_id: playerCardId,
      team_id: teamId,
      game_id: gameId,
      date,
      stat_key,
      stat_value,
      season: season ?? null,
    }));
    if (rows.length === 0) return;

    const { error: insertError } = await supabase.from('sport_stats').insert(rows);

    if (insertError) {
      console.error('[SportStatsService] Error inserting game stats:', insertError);
      throw insertError;
    }
  }

  /**
   * Update an existing stat
   */
  async updateStat(
    statId: string,
    updates: {
      stat_value?: number;
      date?: string;
      game_id?: string;
      season?: string;
    }
  ): Promise<SportStat> {
    const { data, error } = await supabase
      .from('sport_stats')
      .update(updates)
      .eq('id', statId)
      .select()
      .single();

    if (error) {
      console.error('[SportStatsService] Error updating stat:', error);
      throw error;
    }

    return data;
  }

  /**
   * Delete a stat
   */
  async deleteStat(statId: string): Promise<void> {
    const { error } = await supabase
      .from('sport_stats')
      .delete()
      .eq('id', statId);

    if (error) {
      console.error('[SportStatsService] Error deleting stat:', error);
      throw error;
    }
  }

  /**
   * Delete all sport_stats for a game (e.g. before replacing with GameChanger import).
   */
  async deleteStatsByGameId(gameId: string): Promise<void> {
    const { error } = await supabase
      .from('sport_stats')
      .delete()
      .eq('game_id', gameId);

    if (error) {
      console.error('[SportStatsService] Error deleting stats by game:', error);
      throw error;
    }
  }

  /**
   * Save ordered plate appearance results for a player in a game (preserves PA order when editing).
   */
  async saveGameBattingSequence(
    gameId: string,
    playerCardId: string,
    teamId: string,
    date: string,
    results: string[]
  ): Promise<void> {
    const { error } = await supabase
      .from('game_batting_sequence')
      .upsert(
        {
          game_id: gameId,
          player_card_id: playerCardId,
          team_id: teamId,
          results: results,
          date: date,
          updated_at: new Date().toISOString(),
        },
        { onConflict: 'game_id,player_card_id' }
      );

    if (error) {
      console.error('[SportStatsService] Error saving game batting sequence:', error);
      throw error;
    }
  }

  /**
   * Get ordered plate appearance results for a player in a game, or null if none stored.
   */
  async getGameBattingSequence(
    gameId: string,
    playerCardId: string
  ): Promise<string[] | null> {
    const { data, error } = await supabase
      .from('game_batting_sequence')
      .select('results')
      .eq('game_id', gameId)
      .eq('player_card_id', playerCardId)
      .maybeSingle();

    if (error) {
      console.error('[SportStatsService] Error fetching game batting sequence:', error);
      throw error;
    }
    if (!data?.results || !Array.isArray(data.results)) return null;
    return data.results as string[];
  }

  /**
   * Delete the batting sequence for a player in a game (e.g. when clearing batting data).
   */
  async deleteGameBattingSequence(gameId: string, playerCardId: string): Promise<void> {
    const { error } = await supabase
      .from('game_batting_sequence')
      .delete()
      .eq('game_id', gameId)
      .eq('player_card_id', playerCardId);

    if (error) {
      console.error('[SportStatsService] Error deleting game batting sequence:', error);
      throw error;
    }
  }
}

export const sportStatsService = new SportStatsService();

